import React, { ReactNode } from 'react';
import { Descriptions, DescriptionsProps, Flex } from 'antd';
import classNames from 'classnames';
import { DescriptionsItemType } from 'antd/es/descriptions';
import EmptyText from '@client/components/EmptyText';
import styles from './styles/Details.module.css';

type AlignLabel = 'top' | 'baseline' | 'middle';

export type DetailsItem = {
	key: string;
	label: string | React.ReactElement;
	value: string | ReactNode;
	alignLabel?: AlignLabel;
	style?: React.CSSProperties;
	maxHeight?: number;
	span?: number;
};

export type DetailsProps = {
	items: DetailsItem[];
	hideHeader?: boolean;
	hideButtons?: boolean;
	title?: string | null | React.ReactNode;
	boldValue?: boolean;
	extra?: React.ReactNode;
	className?: string;
	clean?: boolean;
	alignLabel?: AlignLabel;
	labelWidth?: number | string;
	column?: number;
	colSpan?: number;
	colon?: boolean;
	bordered?: boolean;
	size?: DescriptionsProps['size'];
} & Omit<DescriptionsItemType, 'children'>;

const Details = ({
	items,
	hideHeader = false,
	hideButtons = false,
	title = 'Details',
	boldValue = false,
	extra,
	className,
	clean = false,
	alignLabel = 'baseline',
	labelWidth,
	colSpan = 3,
	column,
	...props
}: DetailsProps) => {
	const mergedColumn = column ?? 3;
	const equalPairColumns = colSpan * 2 === mergedColumn;
	const labelWidthCss = labelWidth == null ?
		undefined :
		(typeof labelWidth === 'number' ? `${labelWidth}px` : labelWidth);

	let header = null;

	if (!hideHeader) {
		header = (
			<Flex
				align="center"
				justify="space-between"
				gap={12}
				wrap="wrap"
			>
				{title}
				{!hideButtons && extra}
			</Flex>
		);
	}

	return (
		<div
			className={styles.detailsRoot}
			style={labelWidthCss != null ?
				{ ['--details-label-width' as string]: labelWidthCss } :
				undefined}
		>
			<Descriptions
				className={
					classNames(
						styles.details,
						className,
						{
							[styles.cleanDetails]: clean,
							[styles.noHeader]: title == null || title === '',
							[styles.equalPairColumns]: equalPairColumns,
						},
					)
				}
				bordered
				title={header}
				size="small"
				column={mergedColumn}
				{...props}
			>
				{items.map(({ label, value, key, span, ...options }) => {
					return (
						<Descriptions.Item
							key={key}
							span={span ?? colSpan}
							label={label}
							labelStyle={{
								verticalAlign: options.alignLabel || alignLabel,
								width: labelWidth,
							}}
							style={options.style}
						>
							{value != null && value !== '' ? (
								<div
									className={classNames({ [styles.boldValue]: boldValue })}
									style={{
										...(options.maxHeight != null ?
											{
												maxHeight: options.maxHeight,
												overflowY: 'auto',
											} :
											{}),
										...options.style,
									}}
								>
									{value}
								</div>
							) : (
								<EmptyText />
							)}
						</Descriptions.Item>
					);
				})}
			</Descriptions>
		</div>
	);
};

export default Details;
